
import EventPublisher from "../utils/EventPublisher";
import SecureRequestExecuter from "./requestExecuters/SecureRequestExecuter";
import SecureResponseHandler from "./ResponseHandlers/SecureResponseHandler";
import ActionResultEvents from "../constants/events/ActionResultEvents";

class UnavailableCalendarEvent {
    constructor(
        startDate,
        endDate
    ) {
        this.startDate = startDate;
        this.endDate = endDate;
    }
}

class MemberCalendarResponse {

    constructor(
        blockedDates,
        reservationRequests,
        activeReservations
    ) {
        this.blockedDates = blockedDates;
        this.reservationRequests = reservationRequests;
        this.activeReservations = activeReservations;
    }
}

class BlockedDate {
    constructor(
        spaceBlockedDateRangeId,
        spaceId,
        startDate,
        endDate,
        created
    ) {
        this.spaceBlockedDateRangeId = spaceBlockedDateRangeId;
        this.spaceId = spaceId;
        this.startDate = startDate;
        this.endDate = endDate;
        this.created = created;
    }
}

class ReservationRequest {
    constructor(
        reservationRequestId,
        advertisementId,
        advertisementTitle,
        spaceId,
        spaceTitle,
        statusId,
        statusName,
        startDate,
        endDate,
        created,
        updated,
        deleted
    ) {
        this.reservationRequestId = reservationRequestId;
        this.advertisementId = advertisementId;
        this.advertisementTitle = advertisementTitle;
        this.spaceId = spaceId;
        this.spaceTitle = spaceTitle;
        this.statusId = statusId;
        this.statusName = statusName;
        this.startDate = startDate;
        this.endDate = endDate;
        this.created = created;
        this.updated = updated;
        this.deleted = deleted;
    }
}

class ActiveReservation {
    constructor(
        reservationRequestId,
        advertisementId,
        advertisementTitle,
        advertisementMemberId,
        spaceId,
        spaceTitle,
        spaceMemberId,
        startDate,
        endDate,
        activated,
        deleted
    ) {
        this.reservationRequestId = reservationRequestId;
        this.advertisementId = advertisementId;
        this.advertisementTitle = advertisementTitle;
        this.advertisementMemberId = advertisementMemberId;
        this.spaceId = spaceId;
        this.spaceTitle = spaceTitle;
        this.spaceMemberId = spaceMemberId;
        this.startDate = startDate;
        this.endDate = endDate;
        this.activated = activated;
        this.deleted = deleted;
    }
}


export default class SpaceReservationAPIClient {
    constructor() {
        this.getMemberCalendarResponseHandler =
            new SecureResponseHandler(
                new Map()
                    .set("SPACE_CALENDAR_COLLECTED", this.onCalendarLoaded));
        this.getUnavailbleCalendarResponseHandler =
            new SecureResponseHandler(
                new Map()
                    .set("SPACE_CALENDAR_COLLECTED", this.onUnvailableCalendarLoaded));
        this.blockSpaceCalendarDatesResponseHandler =
            new SecureResponseHandler(
                new Map()
                    .set("SPACE_BLOCK_DATES_ADDED", this.onSpaceEventsChanged));
        this.unblockSpaceCalendarDatesResponseHandler =
            new SecureResponseHandler(
                new Map()
                    .set("UNBLOCKED_SPACE_DATES", this.onSpaceEventsChanged));
        this.createReservationRequestResponseHandler =
            new SecureResponseHandler(
                new Map()
                    .set("RESERVATION_REQUEST_CREATED", this.onReservationRequestCreated));

    }

    createReservationRequest(
        spaceId,
        advertisementId,
        startDate,
        endDate
    ) {
        SecureRequestExecuter.executePOSTRequest(
            "/api/private/reservations/request",
            {},
            {
                "space_id": spaceId,
                "advertisement_id": advertisementId,
                "start_date": startDate,
                "end_date": endDate
            },
            {
            },
            this.createReservationRequestResponseHandler
        )
    }

    blockSpaceCalendarDates(spaceId, startDate, endDate) {
        SecureRequestExecuter.executePOSTRequest(
            "/api/private/space/block_dates",
            {},
            {
                "space_id": spaceId,
                "start_date": startDate,
                "end_date": endDate
            },
            {
                spaceId: spaceId
            },
            this.blockSpaceCalendarDatesResponseHandler
        )
    }

    unblockSpaceCalendarDates(spaceId, startDate, endDate) {
        SecureRequestExecuter.executePOSTRequest(
            "/api/private/space/unblock_dates",
            {},
            {
                "space_id": spaceId,
                "start_date": startDate,
                "end_date": endDate
            },
            {
                spaceId: spaceId
            },
            this.unblockSpaceCalendarDatesResponseHandler
        )
    }

    blockAllAvailableSpaceCalendarDates(spaceId) {
        SecureRequestExecuter.executePOSTRequest(
            "/api/private/space/block_all",
            {},
            {
                "space_id": spaceId
            },
            {
                spaceId: spaceId
            },
            this.blockSpaceCalendarDatesResponseHandler
        )
    }

    unblockAllSpaceCalendarDates(spaceId) {
        SecureRequestExecuter.executePOSTRequest(
            "/api/private/space/unblock_all",
            {},
            {
                "space_id": spaceId
            },
            {
                spaceId: spaceId
            },
            this.unblockSpaceCalendarDatesResponseHandler
        )
    }

    getMemberSpaceCalendar(spaceId) {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/space/member_calendar/"+spaceId,
            {},
            {},
            this.getMemberCalendarResponseHandler);
    }

    getUnavailbleSpaceCalendar(spaceId) {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/space/calendar/"+spaceId,
            {},
            {},
            this.getUnavailbleCalendarResponseHandler);
    }

    onSpaceEventsChanged = (responseHeaders, responseBodyContents, dataFromAction) => {
        this.getMemberSpaceCalendar(dataFromAction.spaceId);
    };

    onCalendarLoaded = (responseHeaders, responseBodyContents, dataFromAction) => {
        const blockedDates = [];
        for (const blockedDate of responseBodyContents["blocked_dates"]) {
            blockedDates.push(
                new BlockedDate(
                    blockedDate["space_blocked_date_range_id"],
                    blockedDate["space_id"],
                    blockedDate["start_date"],
                    blockedDate["end_date"],
                    blockedDate["created"]
                )
            );
        }
        const reservationRequests = [];
        for (const reservationRequest of responseBodyContents["reservation_requests"]) {
            reservationRequests.push(
                new ReservationRequest(
                    reservationRequest["reservation_request_id"],
                    reservationRequest["advertisement_id"],
                    reservationRequest["advertisement_title"],
                    reservationRequest["space_id"],
                    reservationRequest["space_title"],
                    reservationRequest["status_id"],
                    reservationRequest["status_name"],
                    reservationRequest["start_date"],
                    reservationRequest["end_date"],
                    reservationRequest["created"],
                    reservationRequest["updated"],
                    reservationRequest["deleted"]
                )
            )
        }
        const activeReservations = [];
        for (const activeReservation of responseBodyContents["active_reservations"]) {
            activeReservations.push(
                new ActiveReservation(
                    activeReservation["reservation_request_id"],
                    activeReservation["advertisement_id"],
                    activeReservation["advertisement_title"],
                    activeReservation["advertisement_member_id"],
                    activeReservation["space_id"],
                    activeReservation["space_title"],
                    activeReservation["space_member_id"],
                    activeReservation["start_date"],
                    activeReservation["end_date"],
                    activeReservation["activated"],
                    activeReservation["deleted"]
                )
            );
        }

        EventPublisher.publish(
            ActionResultEvents.MEMBER_SPACE_CALENDAR_LOADED,
            new MemberCalendarResponse(blockedDates, reservationRequests, activeReservations));
    };

    onUnvailableCalendarLoaded = (responseHeaders, responseBodyContents, dataFromAction) => {
        const unAvailableDates = [];
        for (const unavailableEvent of responseBodyContents["unavailable_dates"]) {
            unAvailableDates.push(
                new UnavailableCalendarEvent(
                    unavailableEvent["start_date"],
                    unavailableEvent["end_date"]
                )
            );
        }

        EventPublisher.publish(
            ActionResultEvents.UNAVAILABLE_SPACE_CALENDAR_LOADED,
            {
                "unavailableDates": unAvailableDates
            }
        );
    };

    onReservationRequestCreated = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(ActionResultEvents.RESERVATION_REQUEST_CREATED_SUCCESSFULLY, {});
    }

}